import { TFunction } from 'i18next';

import { ProfileBody } from '../api/generated/pap';
import featureFlags from './featureFlags';

/**
 * Builds a `profileId -> ProfileBody` lookup map so components that only hold
 * profile ids (e.g. a user's `profileIds`) can resolve the API-provided
 * `displayName` / `description` strings for display.
 */
export const buildProfileMap = (profiles: ProfileBody[]): Map<string, ProfileBody> =>
  new Map(profiles.map((profile) => [profile.profileId, profile]));

/**
 * Resolves the display name for a profile, controlled by the
 * `VITE_FF_PROFILE_API_DISPLAY_STRINGS` feature flag.
 *
 * When the flag is `true` the API-provided `displayName` is returned.
 * When `false` the local `profile` i18n namespace is used instead,
 * keyed by `profileId` (e.g. `dealer.company.administrator`).
 */
export const resolveProfileDisplayName = (profile: ProfileBody, t: TFunction): string =>
  featureFlags.PROFILE_API_DISPLAY_STRINGS
    ? (profile.displayName ?? profile.profileId)
    : t(profile.profileId, { ns: 'profile' });

/**
 * Resolves the description for a profile using the same feature flag as
 * `resolveProfileDisplayName`.  The local i18n key is `<profileId>_description`.
 */
export const resolveProfileDescription = (profile: ProfileBody, t: TFunction): string =>
  featureFlags.PROFILE_API_DISPLAY_STRINGS
    ? (profile.description ?? '')
    : t(`${profile.profileId}_description`, { ns: 'profile' });
